import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPartnerInventoryHealth } from "../../api/partnerPortalApi";
import PartnerVehicleIdentityHealthSection from "../../components/partner/inventory/PartnerVehicleIdentityHealthSection";
import PartnerInventoryRolloutReadinessTable from "../../components/partner/inventory/PartnerInventoryRolloutReadinessTable";
import PartnerInventoryDataSourcesSection from "../../components/partner/inventory/PartnerInventoryDataSourcesSection";
import {
  PARTNER_INVENTORY_ANALYTICS_EVENTS,
  trackPartnerInventoryAnalyticsEvent,
} from "../../utils/partnerInventoryAnalytics";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  PARTNER_INVENTORY_ARCHIVED_STATUS_FILTERS,
  PARTNER_INVENTORY_ISSUE_FILTERS,
  partnerInventoryIssueLabel,
  partnerInventoryListingStatusLabel,
} from "../../constants/partnerInventoryHealthConstants";
import {
  formatIssueProgress,
  formatPublicReadyProgress,
} from "../../utils/partnerInventoryHealthProgress";
import "../../styles/partnerPortalDashboard.css";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "missing", label: "Missing data" },
  { id: "archived", label: "Archived" },
  { id: "dealers", label: "By dealer" },
  { id: "rollout", label: "Rollout" },
  { id: "public-ready", label: "Public-ready" },
  { id: "data-sources", label: "Data sources" },
];

function formatPriceCop(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function ProgressDelta({ progress }) {
  if (!progress?.text) return null;
  return (
    <small className={`partner-progress-delta partner-progress-delta--${progress.tone || "neutral"}`}>
      {progress.text}
    </small>
  );
}

function InventoryTrendChart({ trend = [] }) {
  if (!trend.length) {
    return <p className="muted">Daily snapshots will appear here as progress is tracked.</p>;
  }

  const maxIssues = Math.max(...trend.map((point) => point.withIssues), 1);

  return (
    <div className="partner-inventory-trend">
      <div className="partner-inventory-trend__legend">
        <span>Public-ready %</span>
        <span>Data issues</span>
      </div>
      <div className="partner-inventory-trend__bars" aria-hidden="true">
        {trend.map((point) => (
          <div key={point.date} className="partner-inventory-trend__day">
            <div className="partner-inventory-trend__pair">
              <div
                className="partner-inventory-trend__bar partner-inventory-trend__bar--ready"
                style={{ height: `${Math.max(8, point.publicReadyPct)}%` }}
              />
              <div
                className="partner-inventory-trend__bar partner-inventory-trend__bar--issues"
                style={{
                  height: `${Math.max(8, Math.round((point.withIssues / maxIssues) * 100))}%`,
                }}
              />
            </div>
            <span className="partner-inventory-trend__label">
              {String(point.date).slice(5).replace("-", "/")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IssueBadges({ issues = [] }) {
  if (!issues.length) {
    return <span className="partner-health-ready-badge">Public-ready</span>;
  }

  return (
    <div className="partner-inventory-card__issues">
      {issues.map((issue) => (
        <span key={issue} className="partner-inventory-issue-chip">
          {partnerInventoryIssueLabel(issue)}
        </span>
      ))}
    </div>
  );
}

function ListingActions({ row, path }) {
  const editHref = path(`dealers/${row.dealerId}/inventory/${row.listingId}/edit`);

  return (
    <div className="partner-health-actions">
      {row.canEdit && row.fixableIssues?.length ? (
        <Link className="partner-health-action" to={editHref}>
          Edit
        </Link>
      ) : null}
      {row.canEdit && !row.fixableIssues?.length ? (
        <Link className="partner-health-action" to={editHref}>
          View
        </Link>
      ) : null}
      {row.sourceUrl ? (
        <a className="partner-health-action" href={row.sourceUrl} target="_blank" rel="noreferrer">
          Source
        </a>
      ) : null}
      {row.automapHref ? (
        <a className="partner-health-action" href={row.automapHref} target="_blank" rel="noreferrer">
          AutoMapa
        </a>
      ) : null}
      <Link
        className="partner-health-action"
        to={path(`dealers/${row.dealerId}/inventory?issue=${row.qualityIssues[0] || "any"}`)}
      >
        Dealer
      </Link>
      <Link
        className="partner-health-action"
        to={path(`dealers/${row.dealerId}/inventory/normalization`)}
      >
        Fix vehicle names
      </Link>
    </div>
  );
}

export default function PartnerInventoryHealthPage() {
  const { path, portalLabel } = usePartnerPortalBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const issue = searchParams.get("issue") || "";
  const dealerId = searchParams.get("dealerId") || "";
  const search = searchParams.get("search") || "";
  const listingStatus = searchParams.get("status") || "";
  const offset = Number(searchParams.get("offset") || 0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadWorkspace = useCallback(async () => {
    if (tab === "data-sources") {
      setLoading(false);
      setError("");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const params = {
        issue: issue || undefined,
        dealerId: dealerId || undefined,
        search: search || undefined,
      };

      if (tab === "missing") {
        params.view = "missing";
        params.offset = offset;
        params.limit = 50;
      } else if (tab === "public-ready") {
        params.view = "public-ready";
        params.offset = offset;
        params.limit = 50;
      } else if (tab === "archived") {
        params.view = "archived";
        params.status = listingStatus || undefined;
        params.offset = offset;
        params.limit = 50;
      } else {
        params.limit = 0;
      }

      const result = await getPartnerInventoryHealth(params);
      setData(result);
    } catch (err) {
      setError(err.message || "Unable to load inventory health workspace.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealerId, issue, listingStatus, offset, search, tab]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const vehicleIdentity = data?.vehicleIdentity;

  useEffect(() => {
    if (!vehicleIdentity || tab !== "overview") return;
    trackPartnerInventoryAnalyticsEvent(
      PARTNER_INVENTORY_ANALYTICS_EVENTS.VEHICLE_IDENTITY_HEALTH_VIEWED,
      {
        dealerId: dealerId || null,
        totalListings: vehicleIdentity.totalListings ?? null,
        makeModelCoverage: vehicleIdentity.coverage?.makeModelCoverage ?? null,
        needsReviewRate:
          vehicleIdentity.totalListings > 0
            ? Number(
                (
                  (vehicleIdentity.needsReviewCount || 0) / vehicleIdentity.totalListings
                ).toFixed(4)
              )
            : null,
      }
    );
  }, [dealerId, tab, vehicleIdentity]);

  const updateParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value == null || value === "") params.delete(key);
      else params.set(key, String(value));
    }
    if (!("offset" in updates)) params.delete("offset");
    if (updates.tab && updates.tab !== "archived") params.delete("status");
    if (updates.tab && updates.tab !== "missing") params.delete("issue");
    setSearchParams(params, { replace: true });
  };

  const inventoryHealth = data?.inventoryHealth;
  const rolloutReadiness = data?.rolloutReadiness;
  const inventoryTotals = inventoryHealth?.totals;
  const inventoryProgress = data?.progress;
  const inventoryTrend = data?.trend || [];
  const progressPeriodDays = inventoryProgress?.periodDays || 7;
  const publicReadyProgress = formatPublicReadyProgress(inventoryProgress, progressPeriodDays);
  const issuesProgress = formatIssueProgress(
    inventoryProgress?.withIssues,
    progressPeriodDays,
    inventoryProgress?.hasBaseline
  );

  const dealers = data?.dealers || [];
  const listings = data?.listings || [];
  const pagination = data?.pagination;

  const issueOptions = useMemo(
    () => PARTNER_INVENTORY_ISSUE_FILTERS.filter((entry) => entry.value !== ""),
    []
  );

  const dealerOptions = useMemo(
    () => [{ value: "", label: "All dealers" }, ...dealers.map((d) => ({ value: d.dealerId, label: d.businessName }))],
    [dealers]
  );

  const inventoryIssueMax = Math.max(
    ...Object.values(inventoryHealth?.issues || { fallback: 0 }),
    inventoryTotals?.withIssues || 0,
    1
  );

  return (
    <div className="partner-health-page">
      <header className="partner-health-hero">
        <div>
          <Link className="cv-button-link" to={path("dashboard")}>
            ← Dashboard
          </Link>
          <p className="partner-dashboard-hero__eyebrow">{portalLabel}</p>
          <h2>Inventory data health</h2>
          <p className="muted partner-inventory-health__intro">
            Review missing photos, prices, and other gaps across your dealer network. Fix listings to
            improve AutoMapa visibility.
          </p>
        </div>
        {inventoryTotals ? (
          <div className="partner-health-hero__stats">
            <div className="partner-leads-stat partner-leads-stat--blue">
              <span>Public-ready</span>
              <strong>{inventoryTotals.publicReadyPct}%</strong>
            </div>
            <div className="partner-leads-stat partner-leads-stat--amber">
              <span>Data issues</span>
              <strong>{inventoryTotals.withIssues.toLocaleString()}</strong>
            </div>
            <div className="partner-leads-stat">
              <span>Listings</span>
              <strong>{inventoryTotals.listings.toLocaleString()}</strong>
            </div>
          </div>
        ) : null}
      </header>

      <div className="partner-health-tabs" role="tablist" aria-label="Inventory health views">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            className={`partner-health-tab${tab === entry.id ? " is-active" : ""}`}
            onClick={() => updateParams({ tab: entry.id, offset: 0 })}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === "data-sources" ? <PartnerInventoryDataSourcesSection active /> : null}

      {tab !== "data-sources" && error ? <p className="admin-data-health-error">{error}</p> : null}

      {tab !== "data-sources" && loading && !data ? (
        <div className="partner-panel partner-health-loading">Loading inventory health…</div>
      ) : null}

      {!loading && tab === "overview" && inventoryHealth ? (
        <div className="partner-health-overview">
          <section className="partner-panel">
            <div className="partner-progress partner-inventory-health__progress">
              <div className="partner-progress__meta">
                <span>Public-ready listings</span>
                <div className="partner-inventory-health__progress-copy">
                  <strong>{inventoryTotals.publicReadyPct}%</strong>
                  <ProgressDelta progress={publicReadyProgress} />
                </div>
              </div>
              <div className="partner-progress__track" aria-hidden="true">
                <div
                  className="partner-progress__fill partner-progress__fill--blue"
                  style={{ width: `${Math.min(100, inventoryTotals.publicReadyPct)}%` }}
                />
              </div>
            </div>

            <div className="partner-inventory-health__trend-block">
              <div className="partner-inventory-health__trend-head">
                <h3 className="partner-inventory-health__subheading">
                  Last {inventoryTrend.length || 7} days
                </h3>
                {inventoryProgress?.hasBaseline ? (
                  <span className="muted">Compared to snapshot from {inventoryProgress.baselineDate}</span>
                ) : (
                  <span className="muted">Tracking starts after 7 days of snapshots</span>
                )}
              </div>
              <InventoryTrendChart trend={inventoryTrend} />
            </div>
          </section>

          <section className="partner-panel">
            <h3>Issue breakdown</h3>
            <p className="muted partner-health-section-intro">
              {issuesProgress?.text || `${inventoryTotals.withIssues} listings need attention`}
            </p>
            <div className="partner-inventory-health__issues">
              {Object.entries(inventoryHealth.issues)
                .filter(([, count]) => count > 0)
                .map(([issueKey, count]) => {
                  const issueDelta = formatIssueProgress(
                    inventoryProgress?.issues?.[issueKey],
                    progressPeriodDays,
                    inventoryProgress?.hasBaseline
                  );
                  return (
                    <div key={issueKey} className="partner-inventory-health__issue-row">
                      <div className="partner-inventory-health__issue-copy">
                        <div className="partner-funnel-row">
                          <span className="partner-funnel-row__label">
                            {partnerInventoryIssueLabel(issueKey)}
                          </span>
                          <div className="partner-funnel-row__track" aria-hidden="true">
                            <div
                              className="partner-funnel-row__fill"
                              style={{ width: `${Math.round((count / inventoryIssueMax) * 100)}%` }}
                            />
                          </div>
                          <strong className="partner-funnel-row__value">{count}</strong>
                        </div>
                        {issueDelta ? <ProgressDelta progress={issueDelta} /> : null}
                      </div>
                      <Link
                        className="partner-inventory-health__issue-link"
                        to={path(`inventory-health?tab=missing&issue=${encodeURIComponent(issueKey)}`)}
                      >
                        Review
                      </Link>
                    </div>
                  );
                })}
            </div>
          </section>

          <PartnerVehicleIdentityHealthSection
            vehicleIdentity={vehicleIdentity}
            path={path}
            dealerId={dealerId}
          />
        </div>
      ) : null}

      {!loading && tab === "rollout" ? (
        <PartnerInventoryRolloutReadinessTable rolloutReadiness={rolloutReadiness} path={path} />
      ) : null}

      {!loading && tab === "dealers" ? (
        <section className="partner-panel partner-health-table-panel">
          <div className="partner-panel__header">
            <h3>Dealers ranked by data issues</h3>
            <span className="muted">{dealers.length} dealers</span>
          </div>
          <div className="partner-health-table-wrap">
            <table className="partner-health-table">
              <thead>
                <tr>
                  <th>Dealer</th>
                  <th>Listings</th>
                  <th>Public-ready</th>
                  <th>Issues</th>
                  <th>Top issue</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dealers.map((dealer) => (
                  <tr key={dealer.dealerId}>
                    <td>
                      <strong>{dealer.businessName}</strong>
                      <span className="partner-health-table__meta">{dealer.dataSource}</span>
                    </td>
                    <td>{dealer.listings}</td>
                    <td>{dealer.publicReadyPct}%</td>
                    <td>{dealer.issueCount}</td>
                    <td>{partnerInventoryIssueLabel(dealer.topIssue)}</td>
                    <td>
                      <Link
                        className="partner-health-action"
                        to={path(
                          `inventory-health?tab=missing&dealerId=${encodeURIComponent(dealer.dealerId)}&issue=${encodeURIComponent(dealer.topIssue)}`
                        )}
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!loading && (tab === "missing" || tab === "public-ready" || tab === "archived") ? (
        <section className="partner-panel partner-health-table-panel">
          <div className="partner-health-toolbar">
            <input
              type="search"
              className="partner-leads-search"
              placeholder="Search vehicle or dealer"
              value={search}
              onChange={(event) => updateParams({ search: event.target.value, offset: 0 })}
            />
            {tab === "missing" ? (
              <select
                className="partner-leads-filter"
                value={issue}
                onChange={(event) => updateParams({ issue: event.target.value, offset: 0 })}
              >
                {issueOptions.map((entry) => (
                  <option key={entry.value || "all"} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </select>
            ) : null}
            {tab === "archived" ? (
              <select
                className="partner-leads-filter"
                value={listingStatus}
                onChange={(event) => updateParams({ status: event.target.value, offset: 0 })}
              >
                {PARTNER_INVENTORY_ARCHIVED_STATUS_FILTERS.map((entry) => (
                  <option key={entry.value || "all"} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </select>
            ) : null}
            <select
              className="partner-leads-filter"
              value={dealerId}
              onChange={(event) => updateParams({ dealerId: event.target.value, offset: 0 })}
            >
              {dealerOptions.map((entry) => (
                <option key={entry.value || "all"} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </div>

          <div className="partner-panel__header">
            <h3>
              {tab === "public-ready"
                ? "Public-ready listings"
                : tab === "archived"
                  ? "Sold and archived listings"
                  : "Listings with missing data"}
            </h3>
            <span className="muted">
              {pagination?.total?.toLocaleString() || 0} listing
              {pagination?.total === 1 ? "" : "s"}
            </span>
          </div>

          <div className="partner-health-table-wrap">
            <table className="partner-health-table">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Dealer</th>
                  <th>Price</th>
                  <th>Status</th>
                  {tab === "archived" ? null : <th>Issues</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.length ? (
                  listings.map((row) => (
                    <tr key={`${row.dealerId}-${row.listingId}`}>
                      <td>
                        <strong>{row.title}</strong>
                        <span className="partner-health-table__meta">
                          {[row.year, row.make, row.model].filter(Boolean).join(" ")}
                        </span>
                      </td>
                      <td>{row.dealerName}</td>
                      <td>{formatPriceCop(row.priceCop)}</td>
                      <td>
                        <span className={`partner-health-status partner-health-status--${row.status}`}>
                          {partnerInventoryListingStatusLabel(row.status)}
                        </span>
                      </td>
                      {tab === "archived" ? null : (
                        <td>
                          <IssueBadges issues={row.qualityIssues} />
                        </td>
                      )}
                      <td>
                        <ListingActions row={row} path={path} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tab === "archived" ? 5 : 6} className="partner-health-empty">
                      No listings match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination?.hasMore || offset > 0 ? (
            <div className="partner-health-pagination">
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={offset <= 0}
                onClick={() => updateParams({ offset: Math.max(0, offset - (pagination?.limit || 50)) })}
              >
                Previous
              </button>
              <span className="muted">
                Showing {offset + 1}–{Math.min(offset + (pagination?.limit || 50), pagination?.total || 0)} of{" "}
                {pagination?.total || 0}
              </span>
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={!pagination?.hasMore}
                onClick={() => updateParams({ offset: offset + (pagination?.limit || 50) })}
              >
                Next
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
